export type RecommendationLevel = 'low' | 'medium' | 'high';

export interface DestinationRecommendationCatalogItem {
  candidateId: string;
  name: string;
  country: string;
  icon: string;
  regions: string[];
  styles: string[];
  minBudgetTier: number;
  maxBudgetTier: number;
  familyFriendly: boolean;
  simplicity: RecommendationLevel;
  crowdLevel: RecommendationLevel;
  noiseLevel: RecommendationLevel;
  largeUrban: boolean;
  nightlifeCore: boolean;
  isolationLevel: RecommendationLevel;
  transitComplexity: RecommendationLevel;
  physicalIntensity: RecommendationLevel;
  exoticFoodDependency: RecommendationLevel;
  englishOrPortugueseAccess: RecommendationLevel;
  extremeHeatRiskMonths: number[];
  extremeColdRiskMonths: number[];
  supportedExperiences: string[];
  reason: { pt: string; en: string };
  tradeoff: { pt: string; en: string };
}

export const DESTINATION_RECOMMENDATION_CATALOG: DestinationRecommendationCatalogItem[] = [
  {
    candidateId: 'praia-forte-salvador',
    name: 'Praia do Forte & Salvador',
    country: 'Brasil',
    icon: 'ri-sun-line',
    regions: ['América do Sul'],
    styles: ['Família', 'Relaxamento', 'Gastronomia', 'Cultural'],
    minBudgetTier: 2,
    maxBudgetTier: 5,
    familyFriendly: true,
    simplicity: 'high',
    crowdLevel: 'medium',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'low',
    physicalIntensity: 'low',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['praia', 'gastronomia', 'história', 'família', 'natureza'],
    reason: {
      pt: 'Combina praia, gastronomia baiana e cultura com deslocamentos simples e ritmo confortável.',
      en: 'It combines beach time, Bahian food and culture with simple transfers and a comfortable pace.',
    },
    tradeoff: {
      pt: 'Salvador pede planejamento de horários e bairros para manter a experiência tranquila.',
      en: 'Salvador benefits from careful timing and neighborhood planning to keep the experience calm.',
    },
  },
  {
    candidateId: 'cartagena-caribbean',
    name: 'Cartagena & Islas del Rosario',
    country: 'Colômbia',
    icon: 'ri-ship-line',
    regions: ['América do Sul', 'Caribe'],
    styles: ['Família', 'Relaxamento', 'Gastronomia', 'Cultural', 'Lua de Mel'],
    minBudgetTier: 2,
    maxBudgetTier: 6,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'medium',
    noiseLevel: 'medium',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'low',
    physicalIntensity: 'low',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['praia', 'gastronomia', 'história', 'ilhas', 'arquitetura'],
    reason: {
      pt: 'Entrega mar, centro histórico e boa gastronomia em uma viagem compacta e fácil de personalizar.',
      en: 'It brings together the sea, a historic center and strong food in a compact, adaptable trip.',
    },
    tradeoff: {
      pt: 'O centro histórico pode ficar movimentado; horários e hospedagem fazem diferença.',
      en: 'The historic center can become busy, so timing and hotel location matter.',
    },
  },
  {
    candidateId: 'aruba-easy-caribbean',
    name: 'Aruba',
    country: 'Aruba',
    icon: 'ri-island-line',
    regions: ['Caribe'],
    styles: ['Família', 'Relaxamento', 'Gastronomia', 'Lua de Mel'],
    minBudgetTier: 3,
    maxBudgetTier: 6,
    familyFriendly: true,
    simplicity: 'high',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'low',
    physicalIntensity: 'low',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['praia', 'família', 'gastronomia', 'descanso', 'passeio de barco'],
    reason: {
      pt: 'É uma opção caribenha prática, com praias acessíveis, boa estrutura e ritmo leve.',
      en: 'It is an easy Caribbean option with accessible beaches, reliable infrastructure and a relaxed pace.',
    },
    tradeoff: {
      pt: 'A experiência é mais orientada a praia e resort do que a imersão cultural profunda.',
      en: 'The experience leans more toward beaches and resorts than deep cultural immersion.',
    },
  },
  {
    candidateId: 'riviera-maya',
    name: 'Riviera Maya',
    country: 'México',
    icon: 'ri-island-line',
    regions: ['Caribe', 'América do Norte'],
    styles: ['Família', 'Relaxamento', 'Aventura', 'Cultural'],
    minBudgetTier: 2,
    maxBudgetTier: 6,
    familyFriendly: true,
    simplicity: 'high',
    crowdLevel: 'high',
    noiseLevel: 'medium',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'low',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['praia', 'cenotes', 'ruínas', 'família', 'resort'],
    reason: {
      pt: 'Reúne resort, praia, cenotes e patrimônio maia com boa estrutura turística.',
      en: 'It combines resorts, beaches, cenotes and Mayan heritage with strong tourism infrastructure.',
    },
    tradeoff: {
      pt: 'As atrações mais conhecidas recebem grande volume de visitantes.',
      en: 'Its best-known attractions receive substantial visitor traffic.',
    },
  },
  {
    candidateId: 'lencois-maranhenses',
    name: 'Lençóis Maranhenses',
    country: 'Brasil',
    icon: 'ri-landscape-line',
    regions: ['América do Sul'],
    styles: ['Aventura', 'Relaxamento', 'Cultural'],
    minBudgetTier: 2,
    maxBudgetTier: 5,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'medium',
    transitComplexity: 'medium',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['natureza', 'lagoas', 'travessias', 'comunidades locais', 'aventura'],
    reason: {
      pt: 'Oferece paisagens únicas e aventura com bases e travessias que podem ser ajustadas ao ritmo do viajante.',
      en: 'It offers singular landscapes and adventure, with bases and crossings that can be adjusted to the traveler’s pace.',
    },
    tradeoff: {
      pt: 'Exige logística terrestre mais cuidadosa e a época das lagoas influencia muito a experiência.',
      en: 'It requires more deliberate ground logistics, and lagoon season strongly affects the experience.',
    },
  },
  {
    candidateId: 'chiang-mai-north-thailand',
    name: 'Chiang Mai & Norte da Tailândia',
    country: 'Tailândia',
    icon: 'ri-ancient-gate-line',
    regions: ['Ásia'],
    styles: ['Aventura', 'Cultural', 'Gastronomia', 'Relaxamento'],
    minBudgetTier: 2,
    maxBudgetTier: 5,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'low',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [3, 4, 5],
    extremeColdRiskMonths: [],
    supportedExperiences: ['templos', 'gastronomia', 'montanhas', 'mercados', 'artesanato'],
    reason: {
      pt: 'Equilibra cultura, comida e natureza sem a intensidade de uma megacidade asiática.',
      en: 'It balances culture, food and nature without the intensity of an Asian megacity.',
    },
    tradeoff: {
      pt: 'Março a maio exige atenção a calor e qualidade do ar.',
      en: 'March through May requires attention to heat and air quality.',
    },
  },
  {
    candidateId: 'luang-prabang',
    name: 'Luang Prabang',
    country: 'Laos',
    icon: 'ri-ancient-pavilion-line',
    regions: ['Ásia'],
    styles: ['Cultural', 'Gastronomia', 'Relaxamento', 'Aventura'],
    minBudgetTier: 2,
    maxBudgetTier: 5,
    familyFriendly: false,
    simplicity: 'medium',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'medium',
    transitComplexity: 'low',
    physicalIntensity: 'low',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [4, 5],
    extremeColdRiskMonths: [],
    supportedExperiences: ['templos', 'gastronomia', 'rio', 'mercados', 'cultura local'],
    reason: {
      pt: 'Tem escala humana, identidade cultural forte e ritmo sereno para explorar de manhã e no fim da tarde.',
      en: 'It has a human scale, strong cultural identity and a calm rhythm suited to mornings and late afternoons.',
    },
    tradeoff: {
      pt: 'Tem menos conexões aéreas e variedade de serviços do que os grandes hubs asiáticos.',
      en: 'It has fewer flight links and services than major Asian hubs.',
    },
  },
  {
    candidateId: 'kyoto-kansai',
    name: 'Kyoto, Nara & Osaka',
    country: 'Japão',
    icon: 'ri-ancient-pavilion-line',
    regions: ['Ásia'],
    styles: ['Cultural', 'Gastronomia', 'Aventura', 'Família'],
    minBudgetTier: 4,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'medium',
    noiseLevel: 'medium',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'medium',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [7, 8],
    extremeColdRiskMonths: [],
    supportedExperiences: ['templos', 'gastronomia', 'trens', 'história', 'bairros tradicionais'],
    reason: {
      pt: 'Entrega patrimônio, gastronomia e contraste regional com uma rota ferroviária lógica.',
      en: 'It delivers heritage, food and regional contrast through a logical rail route.',
    },
    tradeoff: {
      pt: 'Os principais templos exigem estratégia de horários para evitar picos de visitação.',
      en: 'The main temples require careful timing to avoid peak visitor periods.',
    },
  },
  {
    candidateId: 'tokyo-metropolitan',
    name: 'Tóquio',
    country: 'Japão',
    icon: 'ri-building-line',
    regions: ['Ásia'],
    styles: ['Cultural', 'Gastronomia', 'Aventura', 'Executivo', 'Luxo'],
    minBudgetTier: 4,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'high',
    noiseLevel: 'high',
    largeUrban: true,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'high',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [7, 8],
    extremeColdRiskMonths: [],
    supportedExperiences: ['gastronomia', 'tecnologia', 'bairros', 'museus', 'compras'],
    reason: {
      pt: 'Oferece variedade excepcional, gastronomia e cultura urbana com organização eficiente.',
      en: 'It offers exceptional variety, food and urban culture with efficient organization.',
    },
    tradeoff: {
      pt: 'É uma megacidade intensa, com multidões, ruído e rede de transporte extensa.',
      en: 'It is an intense megacity with crowds, noise and an extensive transport network.',
    },
  },
  {
    candidateId: 'maldives-indian-ocean',
    name: 'Maldivas',
    country: 'Oceano Índico',
    icon: 'ri-sparkling-2-line',
    regions: [],
    styles: ['Lua de Mel', 'Relaxamento', 'Luxo'],
    minBudgetTier: 5,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'high',
    transitComplexity: 'medium',
    physicalIntensity: 'low',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['resort', 'recife', 'spa', 'mergulho', 'descanso'],
    reason: {
      pt: 'Prioriza descanso, mar e serviço de resort em uma experiência concentrada na ilha escolhida.',
      en: 'It prioritizes rest, the sea and resort service in an experience centered on the selected island.',
    },
    tradeoff: {
      pt: 'É remoto, tem pouca variedade fora do resort e demanda orçamento elevado.',
      en: 'It is remote, offers limited variety beyond the resort and requires a high budget.',
    },
  },
  {
    candidateId: 'oman-dhofar',
    name: 'Dhofar & Salalah',
    country: 'Omã',
    icon: 'ri-landscape-line',
    regions: ['Oriente Médio'],
    styles: ['Luxo', 'Cultural', 'Gastronomia', 'Relaxamento', 'Aventura'],
    minBudgetTier: 4,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'low',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'medium',
    transitComplexity: 'low',
    physicalIntensity: 'low',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [],
    supportedExperiences: ['paisagens', 'cultura árabe', 'gastronomia', 'praia', 'wadis'],
    reason: {
      pt: 'Propõe um Oriente Médio mais sereno, com paisagem, cultura e hotelaria forte sem foco em megacidade.',
      en: 'It offers a calmer Middle East experience with landscape, culture and strong hotels rather than a megacity focus.',
    },
    tradeoff: {
      pt: 'A melhor execução depende de carro ou motorista e de boa seleção de bases.',
      en: 'The best execution depends on a car or driver and carefully selected bases.',
    },
  },
  {
    candidateId: 'istanbul-cappadocia',
    name: 'Istambul & Capadócia',
    country: 'Turquia',
    icon: 'ri-ancient-gate-line',
    regions: ['Oriente Médio'],
    styles: ['Luxo', 'Cultural', 'Gastronomia', 'Aventura'],
    minBudgetTier: 3,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'medium',
    noiseLevel: 'medium',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'medium',
    physicalIntensity: 'medium',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [],
    extremeColdRiskMonths: [1, 2],
    supportedExperiences: ['história', 'gastronomia', 'paisagem', 'arquitetura', 'balão'],
    reason: {
      pt: 'Combina patrimônio, gastronomia e paisagem em duas bases complementares e bem justificadas.',
      en: 'It combines heritage, food and landscape through two complementary, well-justified bases.',
    },
    tradeoff: {
      pt: 'A troca de base e o voo doméstico pedem organização, e atrações famosas podem ficar movimentadas.',
      en: 'The base change and domestic flight require organization, and famous sights can be busy.',
    },
  },
  {
    candidateId: 'jordan-classic-route',
    name: 'Amã, Petra & Wadi Rum',
    country: 'Jordânia',
    icon: 'ri-landscape-line',
    regions: ['Oriente Médio'],
    styles: ['Cultural', 'Aventura', 'Gastronomia', 'Luxo'],
    minBudgetTier: 3,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'medium',
    crowdLevel: 'medium',
    noiseLevel: 'low',
    largeUrban: false,
    nightlifeCore: false,
    isolationLevel: 'medium',
    transitComplexity: 'medium',
    physicalIntensity: 'high',
    exoticFoodDependency: 'medium',
    englishOrPortugueseAccess: 'medium',
    extremeHeatRiskMonths: [6, 7, 8],
    extremeColdRiskMonths: [],
    supportedExperiences: ['arqueologia', 'deserto', 'gastronomia', 'história', 'paisagem'],
    reason: {
      pt: 'Forma uma rota cultural e paisagística forte, com patrimônio e deserto em sequência lógica.',
      en: 'It creates a strong cultural and scenic route, linking heritage and desert in a logical sequence.',
    },
    tradeoff: {
      pt: 'Inclui calor sazonal, trechos terrestres e caminhadas relevantes em Petra.',
      en: 'It includes seasonal heat, road travel and meaningful walking in Petra.',
    },
  },
  {
    candidateId: 'dubai-urban-luxury',
    name: 'Dubai',
    country: 'Emirados Árabes Unidos',
    icon: 'ri-building-4-line',
    regions: ['Oriente Médio'],
    styles: ['Luxo', 'Executivo', 'Família', 'Gastronomia'],
    minBudgetTier: 4,
    maxBudgetTier: 7,
    familyFriendly: true,
    simplicity: 'high',
    crowdLevel: 'high',
    noiseLevel: 'medium',
    largeUrban: true,
    nightlifeCore: false,
    isolationLevel: 'low',
    transitComplexity: 'medium',
    physicalIntensity: 'low',
    exoticFoodDependency: 'low',
    englishOrPortugueseAccess: 'high',
    extremeHeatRiskMonths: [5, 6, 7, 8, 9],
    extremeColdRiskMonths: [],
    supportedExperiences: ['hotelaria', 'arquitetura', 'gastronomia', 'compras', 'deserto'],
    reason: {
      pt: 'Tem hotelaria, serviços e logística previsíveis para uma experiência urbana de alto padrão.',
      en: 'It offers predictable hotels, services and logistics for a high-end urban experience.',
    },
    tradeoff: {
      pt: 'É uma grande cidade, recebe muitos visitantes e tem calor extremo entre maio e setembro.',
      en: 'It is a large city, receives many visitors and has extreme heat from May through September.',
    },
  },
];
